import moment from 'moment';

import { OPERATOR_VALUES, MATCHERS } from '../containers/ComplexDocumentDesign/BusinessLogicTab';
import { VARIABLE_TYPE } from './constants/appConstants';
import { deepCopy } from './javascript';
import {
  BARCODE_ELEMENT,
  CREATIVE_BLOCK,
  DYNAMIC_TABLE_BLOCK,
  FILLABLE_CHECKBOX_TYPE,
  FILLABLE_COMBO_BOX_TYPE,
  FILLABLE_ELEMENT,
  FILLABLE_RADIO_BUTTON_TYPE,
  FILLABLE_TEXT_INPUT_TYPE,
  HTML_BLOCK,
  IMAGE_ELEMENT,
  STANDALONE_BLOCK,
  STATIC_TABLE_BLOCK,
  STATIC_TABLE_ELEMENT,
  TABLE_OF_CONTENTS,
  TEXT_BLOCK,
  TEXT_ELEMENT,
} from './complexDocumentConstants';
import { fetchChart, getChartPayload } from '../containers/ComplexDocumentDesign/CreativeBlockDesign/Charts/chartHelper';
import { barChartTypes } from '../containers/ComplexDocumentDesign/CreativeBlockDesign/Charts/chartConstants';


export const isStringNumber = value => {
  const number = Number(value);
  return !Number.isNaN(number);
};

const compareOperations = (dataSetValue, operator, value) => {
  if (
    operator !== OPERATOR_VALUES.EQUALS_ANY_OF &&
    isStringNumber(dataSetValue) &&
    isStringNumber(value)
  ) {
    dataSetValue = Number(dataSetValue);
    value = Number(value);
  }
  switch (operator) {
    case OPERATOR_VALUES.EQUALS:
      return dataSetValue === value;

    case OPERATOR_VALUES.NOT_EQUALS:
      return dataSetValue !== value;

    case OPERATOR_VALUES.EQUALS_ANY_OF:
      return value.split(',').includes(dataSetValue);

    case OPERATOR_VALUES.IS_EMPTY:
      return dataSetValue.trim() === '';

    case OPERATOR_VALUES.GREATER_THAN:
      return dataSetValue > value;

    case OPERATOR_VALUES.GREATER_THAN_OR_EQUAL:
      return dataSetValue >= value;

    case OPERATOR_VALUES.LESSER_THAN:
      return dataSetValue < value;

    case OPERATOR_VALUES.LESSER_THAN_OR_EQUAL:
      return dataSetValue <= value;

    default:
      break;
  }
};

const getConditionValues = (condition, dataSet, variableContract) => {
  const variableName = variableContract?.find(dVar => dVar.variableId === condition.variableId)
    ?.name;
  const dataSetValue = dataSet[variableName];
  const operator = condition.operator;
  const value = condition.value;
  return { dataSetValue, operator, value };
};

export const filterBlocksByBusinessLogic = (complexDocument, dataSet) => {
  complexDocument.flows = complexDocument.flows.map(flow => {
    flow.blocks = flow.blocks
      .map(block => {
        let keyPrefix = 'system.display.';
        let shouldKeepBlock = true;

        if (block.blockType === STANDALONE_BLOCK) {
          keyPrefix = 'system.display.S';
          block.blockDTOList = block.blockDTOList.filter(blockDTO => {
            const dtoKey = keyPrefix + blockDTO.blockId;
            return !(dtoKey in dataSet) || dataSet[dtoKey] !== "false";
          });

          // If all DTOs are filtered out, consider removing block itself (optional)
          if (block.blockDTOList.length === 0) {
            shouldKeepBlock = false;
          }
        } else {
          const blockKey = keyPrefix + block.blockId;
          if (blockKey in dataSet && dataSet[blockKey] === "false") {
            shouldKeepBlock = false;
          }
        }

        return shouldKeepBlock ? block : null;
      })
      .filter(Boolean); // Remove nulls

    return flow;
  });

  return complexDocument;
};

export const filterFlowByBusinessLogic = (complexDocument, dataSet) => {
  complexDocument.flows = complexDocument.flows.filter(flow => {
    let keyPrefix = 'system.display.flow.';
    const dtoKey = keyPrefix + flow.flowId;
    return !(dtoKey in dataSet) || dataSet[dtoKey] !== 'false';
  });
  return complexDocument;
};

export const filterElementsByBusinessLogic = (complexDocument, dataSet) => {
  complexDocument.flows = complexDocument.flows.map(flow => {
    flow.blocks = flow.blocks.map(block => {
      if (block.blockType === CREATIVE_BLOCK) {
        block.contents = block.contents.filter(element => {
          let keyPrefix = 'system.display.element.';
          const dtoKey = keyPrefix + element.groupId;
          return !(dtoKey in dataSet) || dataSet[dtoKey] !== 'false';
        });
      }
      return block;
    });
    if (flow?.header) {
      if (!flow.header.isStandalone) {
        flow.header.contents = flow.header.contents.filter(element => {
          let keyPrefix = 'system.display.element.';
          const dtoKey = keyPrefix + element.groupId;
          return !(dtoKey in dataSet) || dataSet[dtoKey] !== 'false';
        });
      }
    }
    if (flow?.footer) {
      if (!flow.footer.isStandalone) {
        flow.footer.contents = flow.footer.contents.filter(element => {
          let keyPrefix = 'system.display.element.';
          const dtoKey = keyPrefix + element.groupId;
          return !(dtoKey in dataSet) || dataSet[dtoKey] !== 'false';
        });
      }
    }
    return flow;
  });
  return complexDocument;
};

export const convertVariablesInTextContents = (contents, globalVariables, dataSet) => {
  let isPreviousList = false;
  return contents
    .flatMap(el => {
      if (el?.style?.isVariable) {
        const varId = parseInt(el.content.replaceAll('{', '').replaceAll('}', ''));
        const variableDetails = globalVariables.find(gVar => gVar.id === varId);
        const placeHolderText = variableDetails?.placeHolderText;
        let actualDataSet = dataSet;
        if (
          dataSet !== undefined &&
          el.style?.isVariable &&
          el.repeatBlockName !== undefined &&
          el.repeatBlockIndex !== undefined
        ) {
          actualDataSet = dataSet[el.repeatBlockName][el.repeatBlockIndex];
        }
        let content = actualDataSet[placeHolderText];

        if (content) {
          if (el.contentType === 'LIST_VARIABLE') {
            isPreviousList = true;
            const tempStyle = { ...el.style, isVariable: false };
            return content.flatMap((a, i) => {
              return [
                { content: '•', contentType: 'LIST_LABEL', style: { ...tempStyle, indent: 0, textIndent: 24 } },
                { content: `${a}`, contentType: 'PARAGRAPH', style: tempStyle },
                {
                  content: '\n',
                  contentType: 'LINE_BREAK',
                  style: { ...tempStyle, list: 'bullet', alignment: 'left' },
                },
              ];
            });
          } else {
            if (variableDetails.type === 'Date') {
              content = moment(content, 'dd-MM-yyyy').format(el?.style?.dateFormat?.toUpperCase());
            }
            el.content = content;
          }
          el.style.isVariable = false;
          if(el.style.variable){
            el.style.variable = false;
          }
        }
      }
      if (isPreviousList) {
        isPreviousList = false;
        if (el.contentType === 'LINE_BREAK') {
          return undefined;
        }
      }
      delete el['repeatBlockIndex'];
      delete el['repeatBlockName'];
      return el;
    })
    .filter(v => v);
};

export const convertTextVariablesInFlows = async (updatedDocument, dataSet, globalVariables, convertOnlyTextBlock) => {
  const newDocument = deepCopy(updatedDocument);

  const updateChartDetails = async copiedContent => {
    const payload = getChartPayload(copiedContent, true);
    try {
      const data = await fetchChart(payload);
      copiedContent.dataUrl = `data:image/png;base64, ${data.base64ImageBytes}`;
      copiedContent.dataRequest = payload;
      return copiedContent
    } catch (e) {
      console.log('Error while chart generation in interactive design conversion :', e);
    }
  };

  const updateCreativeBlock = async block => {
    const updatedContents = await Promise.all(
      block.contents.map(async blockContent => {
        if (blockContent.contentType === TEXT_ELEMENT) {
          blockContent.contents = convertVariablesInTextContents(
            blockContent.contents,
            globalVariables,
            dataSet,
          );
        } else if (blockContent.contentType === FILLABLE_ELEMENT) {
          if (blockContent.fillableType === FILLABLE_TEXT_INPUT_TYPE) {
            blockContent.contents = convertVariablesInTextContents(
              blockContent.contents,
              globalVariables,
              dataSet,
            );
          } else if (blockContent.fillableType === FILLABLE_COMBO_BOX_TYPE) {
            blockContent.contents = blockContent.contents.map( (element, index) => {
              const comboBoxOption = convertVariablesInTextContents(
                element.content,
                globalVariables,
                dataSet,
              );
              element.content = comboBoxOption;
              element.defaultValue = parseInt(dataSet[blockContent.formOptions.fieldName]) === index+1;
              return element;
            })
          } else if ([FILLABLE_RADIO_BUTTON_TYPE, FILLABLE_CHECKBOX_TYPE].includes(blockContent.fillableType) && blockContent.style.isVariable) {
            const value = dataSet[blockContent.formOptions.groupName]
            blockContent.formOptions = {...blockContent.formOptions, isChecked: blockContent.fillableType === FILLABLE_CHECKBOX_TYPE && value ? value.includes(blockContent.formOptions.fieldName) : blockContent.formOptions.fieldName === value };
            blockContent.style.isVariable = false;
            if(blockContent.style.variable) {
              blockContent.style.variable = false;
            }
          }
        } else if (blockContent.contentType === STATIC_TABLE_ELEMENT) {
          blockContent.contents = blockContent.contents.map(staticContent => {
            staticContent.contents = convertVariablesInTextContents(
              staticContent.contents,
              globalVariables,
              dataSet,
            );
            return staticContent;
          });
        } else if ([IMAGE_ELEMENT, BARCODE_ELEMENT].includes(blockContent.contentType) && blockContent.style.isVariable) {
          const {repeatBlockIndex, repeatBlockName } = blockContent;
          const varId = parseInt(blockContent.content?.replaceAll('{', '')?.replaceAll('}', ''));
          const variableDetails = globalVariables.find(gVar => gVar.id === varId);
          const placeHolderText = variableDetails?.placeHolderText;

          if (dataSet && dataSet[repeatBlockName]) {
            if(blockContent.contentType === IMAGE_ELEMENT) {
              blockContent.url = dataSet[repeatBlockName][repeatBlockIndex][placeHolderText]
            }else {
              blockContent.content = dataSet[repeatBlockName][repeatBlockIndex][placeHolderText]
            }
  
            delete blockContent['repeatBlockIndex'];
            delete blockContent['repeatBlockName'];
          } else {
            if(blockContent.contentType === IMAGE_ELEMENT) {
              blockContent.url = dataSet[placeHolderText];
            }else {
              blockContent.content = dataSet[placeHolderText];
            }
            
          }
          blockContent.style.isVariable = false;
          if(blockContent.style.variable) {
            blockContent.style.variable = false;
          }
        } else if (blockContent.contentType === 'chart') {
          const { baseVariable, labelVariable, valueVariable, descVariable, dataSource, colorPalette, seriesNameVariable, seriesValueVariable, seriesList} = blockContent.contents;
          const {repeatBlockIndex, repeatBlockName } = blockContent;

          if (
            baseVariable &&
            dataSet &&
            (dataSet[baseVariable] || dataSet[repeatBlockName])
          ) {
            let repeatDataset = dataSet;
            
            if (repeatBlockName && dataSet[repeatBlockName]) {
              repeatDataset = dataSet[repeatBlockName][repeatBlockIndex];
            }
            const copiedContent = deepCopy(blockContent.contents);
            if (barChartTypes.includes(blockContent.contents.type)) {
              const labelKey = globalVariables.find(gVar => gVar.id === labelVariable)?.placeHolderText;
              const sNameKey = globalVariables.find(gVar => gVar.id === seriesNameVariable)
                ?.placeHolderText;
              const sValueKey = globalVariables.find(gVar => gVar.id === seriesValueVariable)
                ?.placeHolderText;
              const previewColorPalette = [...seriesList.map(({ color }) => color), ...colorPalette];
              const previewLabelsSet = new Set();
              const previewNameSet = new Set();
              repeatDataset[baseVariable].forEach(item => {
                previewLabelsSet.add(item[labelKey]);
                previewNameSet.add(item[sNameKey]);
              });
              const previewLabels = Array.from(previewLabelsSet);
              const previewNameList = Array.from(previewNameSet);
              const previewNameMap = new Map();
              previewNameList.forEach(name => {
                previewNameMap.set(name, []);
                previewLabels.forEach(label => {
                  const val = repeatDataset[baseVariable].find(
                    item => item[labelKey] === label && item[sNameKey] === name,
                  );
                  const value = val && val[sValueKey] ? val[sValueKey] : '';
                  const tempArr = previewNameMap.get(name);
                  tempArr.push(value);
                  previewNameMap.set(name, tempArr);
                });
              });
      
              // logic to add colors to chart on preview when not enough colors available in color palette.
              let colorIndex = 0;
              let colorDivideNum = 1;
              const previewSeriesList = previewNameList.map(name => {
                if (!previewColorPalette[colorIndex]) {
                  colorIndex = 0;
                  colorDivideNum *= 2;
                }
                const color = previewColorPalette[colorIndex];
                const colorAfterDivision = color.map(num => Math.floor(num / colorDivideNum));
                colorIndex += 1;
                return {
                  name: name,
                  series: previewNameMap.get(name),
                  color: colorAfterDivision,
                };
              });
              copiedContent.seriesList = previewSeriesList;
              copiedContent.labels = previewLabels;
            } else {
              const labelKey = globalVariables.find(gVar => gVar.id === labelVariable)?.placeHolderText;
              const valueKey = globalVariables.find(gVar => gVar.id === valueVariable)?.placeHolderText;
              const descKey = globalVariables.find(gVar => gVar.id === descVariable)?.placeHolderText;
              const previewColorPalette = [...dataSource.map(({ color }) => color), ...colorPalette];
              // logic to add colors to chart on preview when not enough colors available in color palette.
              let colorIndex = 0;
              let colorDivideNum = 1;
              copiedContent.dataSource = repeatDataset[baseVariable].map(v => {
                if (!previewColorPalette[colorIndex]) {
                  colorIndex = 0;
                  colorDivideNum *= 2;
                }
                const color = previewColorPalette[colorIndex];
                const colorAfterDivision = color.map(num => Math.floor(num / colorDivideNum));
                const retObj = {
                  label: v[labelKey],
                  value: v[valueKey],
                  description: v[descKey],
                  color: colorAfterDivision,
                };
                colorIndex += 1;
                return retObj;
              });
            }
            blockContent.contents = await updateChartDetails(copiedContent);
            blockContent.style.isVariable = false;
            if(blockContent.style.variable) {
              blockContent.style.variable = false;
            }
            delete blockContent['repeatBlockIndex'];
            delete blockContent['repeatBlockName'];
          }
        }
        return blockContent;
      })
    );
    block.contents = updatedContents;
    return block;
  };

  const handleBlocks = async block => {
    const isTextBlock = block.blockType === TEXT_BLOCK;
    const isCreativeBlock = block.blockType === CREATIVE_BLOCK;
    const isDynamicTable = block.blockType === DYNAMIC_TABLE_BLOCK;
    const isStaticTable = block.blockType === STATIC_TABLE_BLOCK;
    const isStandalone = block.blockType === STANDALONE_BLOCK;

    if (isTextBlock && convertOnlyTextBlock && block.isEditable) {
      block.contents = convertVariablesInTextContents(block.contents, globalVariables, dataSet);
      block.variableContract =[];
      return block;
    }

    if (isTextBlock && !convertOnlyTextBlock && !block.isEditable) {
      block.contents = convertVariablesInTextContents(block.contents, globalVariables, dataSet);
      return block
    }

    if (convertOnlyTextBlock) return block;

    if (isCreativeBlock) {
      await updateCreativeBlock(block);
    } else if (isDynamicTable && Array.isArray(block.blockDTOList)) {
      block.blockDTOList = await Promise.all(block.blockDTOList.map(updateCreativeBlock));
      block.contents = await Promise.all(block.contents.map(content => {
        const obj = { ...content };
        if (content.groupHeaderContent) {
          obj.groupHeaderContent = {
            ...content.groupHeaderContent,
            contents: convertVariablesInTextContents(
              content.groupHeaderContent.contents,
              globalVariables,
              dataSet,
            ),
          };
        }
        if (content.headerContent) {
          obj.headerContent = {
            ...content.headerContent,
            contents: convertVariablesInTextContents(
              content.headerContent.contents,
              globalVariables,
              dataSet,
            ),
          };
        }
        return obj;
      }));
    } else if (isStaticTable && Array.isArray(block.contents)) {
      block.contents = await Promise.all(
        block.contents.map(async cell => {
          const cellData = cell.contents?.[0];
          if (!cellData) return cell;

          if (cellData.contentType === TEXT_BLOCK) {
            cell.contents[0].contents = convertVariablesInTextContents(
              cell.contents[0].contents,
              globalVariables,
              dataSet,
            );
          } else {
            await updateCreativeBlock({ contents: cell.contents });
          }

          return cell;
        }),
      );
    } else if (isStandalone && Array.isArray(block.blockDTOList)) {
      block.blockDTOList = await Promise.all(block.blockDTOList.map(handleBlocks));
    }

    return block;
  };
  

  const processHeaderOrFooter = async section => {
    if (!section) return null;
    if (section.isStandalone) {
      section.blockDTOList = await Promise.all(
        section.blockDTOList.map(updateCreativeBlock)
      );
      return section;
    }
    return await updateCreativeBlock(section);
  };


  newDocument.flows = await Promise.all(
    newDocument.flows.map(async flow => {
      if(flow.flowName === TABLE_OF_CONTENTS) return flow;

      flow.blocks = await Promise.all(flow.blocks.map(handleBlocks));
      flow.header = await processHeaderOrFooter(flow.header);
      flow.footer = await processHeaderOrFooter(flow.footer);
      
      return flow;
    })
  )

  return newDocument;
};

export const convertStandaloneFlowBlocks = newDocument =>{
  newDocument.flows.forEach(flow => {
    if (flow.flowName !== TABLE_OF_CONTENTS) {
      flow.isStandalone =false;
      if (flow.header) {
        if(flow.header.blockType === STANDALONE_BLOCK){
          flow.header = {...flow.header, ...flow.header.blockDTOList[0]}
          flow.header.isStandalone = false;
          delete flow.header.blockDTOList;
        }
      }
      if (flow.blocks) {
        let blockData = [];
        let blockNumber = 1;

        flow.blocks.forEach(block => {
          if (block.blockType === STANDALONE_BLOCK) {
            block.blockDTOList.forEach(referencedBlock => {
              blockNumber += 1;
              blockData.push({ ...referencedBlock, isStandalone: false, orderNumber: blockNumber });
            });
          } else {
            blockNumber +=1;
            blockData.push({...block, orderNumber: blockNumber});
          }
        });

        flow.blocks = blockData;
      }
      if (flow.footer) {
        if(flow.footer.blockType === STANDALONE_BLOCK){
          flow.footer = {...flow.footer, ...flow.footer.blockDTOList[0]}
          flow.footer.isStandalone = false;
          delete flow.footer.blockDTOList;
        }
      }
    }
  });
  return newDocument;
}

const formatRepeatBlocks = (block, orderNumber, dataSetKey, dataSetValues) => {
  let updatedOrderNumber = orderNumber;
  const formattedBlocks=  dataSetValues.map((dataSetValue, index) => {
    updatedOrderNumber = orderNumber + index;
    let newBlock;
    if (block.blockType === VARIABLE_TYPE.DYNAMIC_TABLE) {
      newBlock = {
        ...block,
        blockId: `${block.blockId}-${index + 1}`,
        orderNumber: updatedOrderNumber,
        dataIndex: index,
        repeatBlockName: dataSetKey,
        repeatBlockIndex: index,
      };
    } else {
      newBlock = {
        ...block,
        blockId: `${block.blockId}-${index + 1}`,
        orderNumber: updatedOrderNumber,
        dataIndex: index,
        contents: block.contents
          ? block.contents.map(content => ({
              ...content,
              ...(content.style.isVariable && {
                repeatBlockName: dataSetKey,
                repeatBlockIndex: index,
              }),
              contents: Array.isArray(content.contents)
                ? content.contents.map(innerContent => ({
                    ...innerContent,
                    ...(innerContent.style.isVariable && {
                      repeatBlockName: dataSetKey,
                      repeatBlockIndex: index,
                    }),
                    contents: Array.isArray(innerContent.contents) // Only for static table cells
                      ? innerContent.contents.map(textContent => ({
                          ...textContent,
                          ...(textContent.style.isVariable && {
                            repeatBlockName: dataSetKey,
                            repeatBlockIndex: index,
                          }),
                        }))
                      : innerContent.contents,
                  }))
                : content.contents,
            }))
          : undefined,
      };
    }
    return newBlock
  });
  return {formattedBlocks, updatedOrderNumber}
};

export const repeatBlocks = (complexDocument, dataSet, repeatOnlyTextBlock) => {
  // Copy the existingDocument object to avoid mutation
  const newDocument = { ...complexDocument };
  let blockNumber = 0;
  newDocument.flows = newDocument.flows.map(flow => ({
    ...flow,
    blocks: flow.blocks.flatMap(block => {
      const blockTypes = [VARIABLE_TYPE.DYNAMIC_TABLE, VARIABLE_TYPE.STANDALONE];
      if (
        !blockTypes.includes(block.blockType) &&
        block.variableContract.some(vc => vc.type === VARIABLE_TYPE.OBJECT)
      ) {
        const dataSetKey = block.variableContract[0].name;
        const dataSetValues = dataSet[dataSetKey] || [];
        // Increment orderNumber for the next block
        blockNumber +=1 
        // Generate blocks based on dataSetValues length
        if(repeatOnlyTextBlock && block.blockType === TEXT_BLOCK && block.isEditable){
          const {formattedBlocks, updatedOrderNumber}= formatRepeatBlocks(block, blockNumber, dataSetKey, dataSetValues);
          blockNumber = updatedOrderNumber;
          return formattedBlocks;
        } else if (!repeatOnlyTextBlock && !block.isEditable) {
          const {formattedBlocks, updatedOrderNumber}= formatRepeatBlocks(block, 1, dataSetKey, dataSetValues);
          blockNumber = updatedOrderNumber;
          const parentBlockId = formattedBlocks[0]?.blockId.replace(/-\d+$/, '');
          return [{
            blockType: 'parent',
            orderNumber: blockNumber,
            isStandalone: true,
            style: {},
            isInteractiveParentBlock: true,
            blockDTOList: formattedBlocks,
            blockId: parentBlockId,
            variableContract: formattedBlocks[0]?.variableContract
          }];
        } else {
          return  {...block, orderNumber : blockNumber}
        }
        
      } else if (!repeatOnlyTextBlock && block.blockType === VARIABLE_TYPE.STANDALONE) {
        blockNumber += 1;
        const blockParent = { ...block, orderNumber: blockNumber };
        let standaloneBlockNumber= 0;
        blockParent.blockDTOList = block.blockDTOList.flatMap(childBlock => {
          const blockTypes = [VARIABLE_TYPE.DYNAMIC_TABLE];
          if (
            !blockTypes.includes(childBlock.blockType) &&
            childBlock.variableContract.some(vc => vc.type === VARIABLE_TYPE.OBJECT)
          ) {
            const dataSetKey = childBlock.variableContract[0].name;
            const dataSetValues = dataSet[dataSetKey] || [];
            standaloneBlockNumber +=1 
            // Generate blocks based on dataSetValues length
            const {formattedBlocks, updatedOrderNumber}= formatRepeatBlocks(childBlock, standaloneBlockNumber, dataSetKey, dataSetValues);
            blockNumber = updatedOrderNumber;
            return formattedBlocks;
          } else {
            standaloneBlockNumber +=1;
            return [{...childBlock, orderNumber: standaloneBlockNumber}];
          }
        });
        return [blockParent];
      } else if (
        !repeatOnlyTextBlock &&
        block.blockType === VARIABLE_TYPE.DYNAMIC_TABLE &&
        block.variableContract.some(vc => vc.type === VARIABLE_TYPE.OBJECT && vc.isArray)
      ) {
        const dataSetKey = block.variableContract[0].name;
        const dataSetValues = dataSet[dataSetKey] || [];
        const { formattedBlocks, updatedOrderNumber } = formatRepeatBlocks(
          block,
          blockNumber,
          dataSetKey,
          dataSetValues,
        );
        blockNumber = updatedOrderNumber;
        return formattedBlocks;
      } else {
        blockNumber += 1;
        return [{...block, orderNumber: blockNumber}];
      }
    }),
  }));
  // Return the updated document
  return newDocument;
};

export const filterInsertsByBusinessLogic = (complexDocument, dataSet) => {
  const inserts = complexDocument.inserts;
  const variableContract = complexDocument.variableContract;

  complexDocument.inserts = inserts.filter(({ businessLogic }) => {
    if (businessLogic) {
      const { conditions } = businessLogic;
      const conditionsPassed = conditions.every(condition => {
        const { dataSetValue, operator, value } = getConditionValues(
          condition,
          dataSet,
          variableContract,
        );
        return value !== '' ? compareOperations(dataSetValue, operator, value) : true;
      });

      const extraConditionsPassed = conditions.every(condition => {
        return (
          condition.extraConditions.length > 0 &&
          condition.extraConditions.every(extraCondition => {
            const { dataSetValue, operator, value } = getConditionValues(
              extraCondition,
              dataSet,
              variableContract,
            );
            return value !== '' ? compareOperations(dataSetValue, operator, value) : true;
          })
        );
      });

      if (conditionsPassed || extraConditionsPassed) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  });
  return complexDocument;
};
